import { useCallback, useEffect, useMemo } from 'react';

import { useSyncExternalStore } from 'use-sync-external-store/shim';

import { DEFAULT_PRODUCT_CONTEXT, type ProductContext } from '../../common/constants';
import {
	AssetsWorkspaceHostStatus,
	type AssetsWorkspaceHostState,
	type UseAssetsWorkspaceHostOptions,
} from '../../common/types';
import { fetchAssetsWorkspaceHost } from '../../services/fetchAssetsWorkspaceHost';
import {
	getMultiSite,
	getServerMultiSite,
	getServerSnapshot,
	getServerUnitsEnabled,
	getServerUnitsExpiresAt,
	getSnapshot,
	getUnitsEnabled,
	getUnitsExpiresAt,
	isStale,
	isUnitsEnabledStale,
	subscribe,
} from '../../state/store';

const IDLE_RESULT: AssetsWorkspaceHostState = {
	status: AssetsWorkspaceHostStatus.Idle,
	data: null,
	error: null,
};

const PENDING_RESULT: AssetsWorkspaceHostState = {
	status: AssetsWorkspaceHostStatus.Pending,
	data: null,
	error: null,
};

const UNAVAILABLE_RESULT: AssetsWorkspaceHostState = {
	status: AssetsWorkspaceHostStatus.Unavailable,
	data: null,
	error: null,
};

// Never expose a cached workspace until the Units check confirms it is enabled.
const resolveStateForUnits = (
	unitsEnabled: boolean | null,
	state: AssetsWorkspaceHostState,
): AssetsWorkspaceHostState => {
	if (state.status === AssetsWorkspaceHostStatus.Unavailable) {
		return state;
	}
	if (unitsEnabled === null) {
		return PENDING_RESULT;
	}
	return unitsEnabled ? state : UNAVAILABLE_RESULT;
};

const absoluteUrlFn = (path: string): string => path;

export type UseAssetsWorkspaceHostResult = AssetsWorkspaceHostState & {
	readonly isIdle: boolean;
	/**
	 * `multiSite` from the `/assets/is-multi-site` check. `null` until the check
	 * resolves, when it failed, or when `requireUnitsEnabled` is not set.
	 */
	readonly isMultiSite: boolean | null;
	readonly isPending: boolean;
	readonly isSuccess: boolean;
	/** `true` only when `requireUnitsEnabled` is set, Units is confirmed enabled and the workspace resolved. */
	readonly isUnitsEnabledForAssets: boolean;
	readonly refetch: () => void;
	/**
	 * The Units-aware Assets site cloud id when {@link isSuccess} is `true` and
	 * the BE supplied one for this tenant, otherwise `null`. Use the `??`
	 * fallback to swap in the current browsing site's `cloudId` at the call
	 * site when this is `null`.
	 */
	readonly resolvedCmdbSiteCloudId: string | null;
	/**
	 * The resolved workspaceId when {@link isSuccess} is `true`, otherwise `null`.
	 * Use the `??` fallback to swap in a legacy site-local workspaceId at the call site.
	 */
	readonly resolvedWorkspaceId: string | null;
	/**
	 * Absolute-URL builder when {@link isSuccess} is `true`. Returns the input
	 * path unchanged (identity) for every other status, so consumers can always
	 * call it without a guard.
	 */
	readonly toResolvedAbsoluteUrl: (relativePath: string) => string;
};

/**
 * React hook returning the resolved Assets workspace coordinates for the
 * given tenant.
 *
 * `cloudId` is mandatory: read it from `useTenantContext()` (or equivalent)
 * at the call site. The cache key, singleflight scope, and TTL window are
 * all per-`cloudId`, so passing the right tenant is the contract that keeps
 * cross-tenant entries from leaking into each other. The BE returns the same
 * `workspaceId` regardless of `productContext`, so all variants for one
 * `cloudId` share a single cache entry.
 *
 * Optional args:
 *  - `isOnlyWorkspace` (default `false`): when `true`, hits the canonical
 *    `/workspace` endpoint with no product context. When `false`, hits
 *    `/internal/workspace?productContext={productContext}`.
 *  - `productContext` (default `'jsm'`): one of `'jira' | 'jsm' | 'confluence'`,
 *    forwarded as the query parameter on `/internal/workspace`. Ignored when
 *    `isOnlyWorkspace` is `true`.
 *  - `requireUnitsEnabled` (default `false`): returns `unavailable` unless
 *    `/assets/is-multi-site` reports Units enabled. Ignored while `skip`.
 *  - `skip` (default `false`): when `true`, the hook returns `idle` and never
 *    subscribes / fetches.
 *
 * SSR-safe: server snapshot is always `pending`, so the slow resolver never
 * blocks server render. The first effect after hydration kicks off the
 * fetch (or reads from the in-memory cache when one is fresh).
 *
 * Cross-component coalesced: every subscriber for the same `cloudId` shares
 * one network call via the module-scoped store.
 */
export const useAssetsWorkspaceHost = (
	options: UseAssetsWorkspaceHostOptions,
): UseAssetsWorkspaceHostResult => {
	const cloudId: string = options.cloudId;
	const isOnlyWorkspace: boolean = options.isOnlyWorkspace === true;
	const productContext: ProductContext = options.productContext ?? DEFAULT_PRODUCT_CONTEXT;
	const skip: boolean = options.skip === true;
	const requireUnitsEnabled: boolean = options.requireUnitsEnabled === true && !skip;

	const subscribeForCloudId = useCallback(
		(listener: () => void) => subscribe(cloudId, listener),
		[cloudId],
	);
	const getSnapshotForCloudId = useCallback(() => getSnapshot(cloudId), [cloudId]);

	const state = useSyncExternalStore(subscribeForCloudId, getSnapshotForCloudId, getServerSnapshot);

	const getUnitsEnabledForCloudId = useCallback(
		() => (requireUnitsEnabled ? getUnitsEnabled(cloudId) : null),
		[cloudId, requireUnitsEnabled],
	);
	const unitsEnabled = useSyncExternalStore(
		subscribeForCloudId,
		getUnitsEnabledForCloudId,
		getServerUnitsEnabled,
	);

	const getMultiSiteForCloudId = useCallback(
		() => (requireUnitsEnabled ? getMultiSite(cloudId) : null),
		[cloudId, requireUnitsEnabled],
	);
	const isMultiSite = useSyncExternalStore(
		subscribeForCloudId,
		getMultiSiteForCloudId,
		getServerMultiSite,
	);

	const getUnitsExpiresAtForCloudId = useCallback(
		() => (requireUnitsEnabled ? getUnitsExpiresAt(cloudId) : null),
		[cloudId, requireUnitsEnabled],
	);
	const unitsExpiresAt = useSyncExternalStore(
		subscribeForCloudId,
		getUnitsExpiresAtForCloudId,
		getServerUnitsExpiresAt,
	);

	// Revalidate on expiry so a mounted consumer recovers without a remount.
	useEffect(() => {
		if (unitsExpiresAt === null) {
			return;
		}
		const timer = setTimeout(
			() => {
				void fetchAssetsWorkspaceHost({
					cloudId,
					isOnlyWorkspace,
					productContext,
					requireUnitsEnabled,
				}).catch(() => {
					// no-op — store already reflects the failure
				});
			},
			Math.max(unitsExpiresAt - Date.now(), 0) + 1,
		);
		return () => clearTimeout(timer);
	}, [cloudId, isOnlyWorkspace, productContext, requireUnitsEnabled, unitsExpiresAt]);

	useEffect(() => {
		if (skip) {
			return;
		}
		if (requireUnitsEnabled && unitsEnabled === false && !isUnitsEnabledStale(cloudId)) {
			// Units is off: nothing to resolve until the check expires.
			return;
		}
		// Trigger fetch on mount, on cloudId change, or when the cached value
		// has gone stale. The fetcher itself is single-flight so multiple
		// hooks won't fan out into multiple network calls.
		//
		// On a cold mount the first paint sees `idle`; the effect synchronously
		// flips the store to `pending` (via `setPending` inside
		// `fetchAssetsWorkspaceHost`) before the network resolves, so the next
		// render reflects in-flight state. Errors land in the store via the
		// fetcher itself; the trailing `.catch` is purely there to avoid an
		// unhandled rejection.
		if (
			state.status === AssetsWorkspaceHostStatus.Idle ||
			(state.status === AssetsWorkspaceHostStatus.Success && isStale(cloudId)) ||
			(requireUnitsEnabled && (unitsEnabled === null || isUnitsEnabledStale(cloudId)))
		) {
			void fetchAssetsWorkspaceHost({
				cloudId,
				isOnlyWorkspace,
				productContext,
				requireUnitsEnabled,
			}).catch(() => {
				// no-op — store already reflects the failure
			});
		}
	}, [
		cloudId,
		isOnlyWorkspace,
		productContext,
		requireUnitsEnabled,
		skip,
		state.status,
		unitsEnabled,
	]);

	const refetch = useCallback(() => {
		if (skip) {
			return;
		}
		// `refetch` must never throw — errors are surfaced through the
		// store-driven re-render as `status: 'error' | 'unavailable'`. The
		// IIFE + try/catch guarantees no unhandled rejection escapes into
		// consumers.
		void (async () => {
			try {
				await fetchAssetsWorkspaceHost({
					cloudId,
					force: true,
					isOnlyWorkspace,
					productContext,
					requireUnitsEnabled,
				});
			} catch {
				// no-op — store already reflects the failure
			}
		})();
	}, [cloudId, isOnlyWorkspace, productContext, requireUnitsEnabled, skip]);

	return useMemo<UseAssetsWorkspaceHostResult>(() => {
		const baseState = skip
			? IDLE_RESULT
			: requireUnitsEnabled
				? resolveStateForUnits(unitsEnabled, state)
				: state;
		const isSuccess = baseState.status === AssetsWorkspaceHostStatus.Success;
		const isPending = baseState.status === AssetsWorkspaceHostStatus.Pending;
		const isIdle = baseState.status === AssetsWorkspaceHostStatus.Idle;
		return {
			...baseState,
			refetch,
			isIdle,
			isMultiSite,
			isPending,
			isSuccess,
			isUnitsEnabledForAssets: requireUnitsEnabled && isSuccess,
			resolvedCmdbSiteCloudId: isSuccess ? baseState.data.cmdbSiteCloudId : null,
			resolvedWorkspaceId: isSuccess ? baseState.data.workspaceId : null,
			toResolvedAbsoluteUrl: isSuccess ? baseState.data.toAbsoluteUrl : absoluteUrlFn,
		};
	}, [skip, requireUnitsEnabled, unitsEnabled, isMultiSite, state, refetch]);
};
